import i18next from 'i18next';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  acceptIntakeSubmission,
  discardOwnIntakeDraft,
  fetchOwnIntakeSubmission,
  fetchOwnIntakeSubmissions,
  fetchPatientIntakeSubmission,
  fetchWorkspaceIntakeSubmission,
  fetchWorkspaceIntakeSubmissions,
  returnIntakeSubmission,
  saveOwnIntakeDraft,
  submitOwnIntake,
} from '@/api/intake/intakeApi';
import type {
  IntakeSubmission,
  IntakeSubmissionListItem,
  OwnerIntakeDraftPayload,
  OwnerIntakeSubmission,
  WorkspaceIntakeSubmissionListItem,
} from '@/api/intake/intakeApi.types';
import type { PaginatedResult } from '@/api/pagination.types';
import {
  buildPatientFirstConsultationQueryKey,
  buildPatientIntakeSubmissionQueryKey,
  buildPatientQueryKey,
  buildPatientTimelineQueryKey,
  OWN_INTAKE_SUBMISSIONS_QUERY_KEY,
  OWN_PORTAL_SUMMARY_QUERY_KEY,
  PATIENTS_QUERY_KEY,
  WORKSPACE_ATTENTION_QUERY_KEY,
  WORKSPACE_INTAKE_SUBMISSIONS_QUERY_KEY,
} from '@/constants/queryKeys';
import { INTAKE_SUBMISSION_STATUS } from '@/constants/intake';

export const WORKSPACE_INTAKE_PAGE_SIZE = 20;

export function useOwnIntakeSubmissionsQuery(): UseQueryResult<IntakeSubmissionListItem[], Error> {
  return useQuery({
    queryKey: [...OWN_INTAKE_SUBMISSIONS_QUERY_KEY, 'list'],
    queryFn: fetchOwnIntakeSubmissions,
  });
}

export function useOwnIntakeSubmissionQuery(
  submissionId: string | null
): UseQueryResult<OwnerIntakeSubmission, Error> {
  const resolvedSubmissionId = submissionId === null ? '' : submissionId;
  return useQuery({
    queryKey: [...OWN_INTAKE_SUBMISSIONS_QUERY_KEY, resolvedSubmissionId, i18next.language],
    queryFn: function load() {
      return fetchOwnIntakeSubmission(resolvedSubmissionId);
    },
    enabled: resolvedSubmissionId.length > 0,
  });
}

export function useWorkspaceIntakeSubmissionsQuery(
  page: number,
  search: string,
  status: string
): UseQueryResult<PaginatedResult<WorkspaceIntakeSubmissionListItem>, Error> {
  return useQuery({
    queryKey: [...WORKSPACE_INTAKE_SUBMISSIONS_QUERY_KEY, 'list', page, search, status],
    queryFn: function load() {
      return fetchWorkspaceIntakeSubmissions(page, WORKSPACE_INTAKE_PAGE_SIZE, search, status);
    },
    placeholderData: keepPreviousData,
  });
}

export function usePendingIntakeCountQuery(isEnabled: boolean): UseQueryResult<number, Error> {
  return useQuery({
    queryKey: [...WORKSPACE_INTAKE_SUBMISSIONS_QUERY_KEY, 'pendingCount'],
    queryFn: async function load() {
      const firstPage = await fetchWorkspaceIntakeSubmissions(
        1,
        1,
        '',
        INTAKE_SUBMISSION_STATUS.submitted
      );
      return firstPage.totalItems;
    },
    enabled: isEnabled,
  });
}

export function useWorkspaceIntakeSubmissionQuery(
  submissionId: string
): UseQueryResult<IntakeSubmission, Error> {
  return useQuery({
    queryKey: [...WORKSPACE_INTAKE_SUBMISSIONS_QUERY_KEY, submissionId, i18next.language],
    queryFn: function load() {
      return fetchWorkspaceIntakeSubmission(submissionId);
    },
    enabled: submissionId.length > 0,
  });
}

interface SaveOwnIntakeDraftVariables {
  submissionId: string | null;
  payload: OwnerIntakeDraftPayload;
}

export function useSaveOwnIntakeDraftMutation(): UseMutationResult<
  OwnerIntakeSubmission,
  Error,
  SaveOwnIntakeDraftVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function save(variables: SaveOwnIntakeDraftVariables) {
      return saveOwnIntakeDraft(variables.submissionId, variables.payload);
    },
    onSuccess: function invalidate() {
      queryClient.invalidateQueries({ queryKey: OWN_INTAKE_SUBMISSIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: OWN_PORTAL_SUMMARY_QUERY_KEY });
    },
  });
}

export function useSubmitOwnIntakeMutation(): UseMutationResult<
  OwnerIntakeSubmission,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitOwnIntake,
    onSuccess: function invalidate() {
      queryClient.invalidateQueries({ queryKey: OWN_INTAKE_SUBMISSIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: OWN_PORTAL_SUMMARY_QUERY_KEY });
    },
  });
}

export function useDiscardOwnIntakeDraftMutation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: discardOwnIntakeDraft,
    onSuccess: function invalidate() {
      queryClient.invalidateQueries({ queryKey: OWN_INTAKE_SUBMISSIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: OWN_PORTAL_SUMMARY_QUERY_KEY });
    },
  });
}

export function usePatientIntakeSubmissionQuery(
  patientId: string
): UseQueryResult<IntakeSubmission | null, Error> {
  return useQuery({
    queryKey: [...buildPatientIntakeSubmissionQueryKey(patientId), i18next.language],
    queryFn: function load() {
      return fetchPatientIntakeSubmission(patientId);
    },
    enabled: patientId.length > 0,
  });
}

interface ReviewIntakeSubmissionVariables {
  submissionId: string;
}

function invalidatePatientIntakeViews(
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: string | null
) {
  queryClient.invalidateQueries({ queryKey: WORKSPACE_INTAKE_SUBMISSIONS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: WORKSPACE_ATTENTION_QUERY_KEY });
  if (patientId === null) {
    return;
  }
  queryClient.invalidateQueries({ queryKey: buildPatientIntakeSubmissionQueryKey(patientId) });
}

export function useAcceptIntakeSubmissionMutation(): UseMutationResult<
  IntakeSubmission,
  Error,
  ReviewIntakeSubmissionVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function accept(variables: ReviewIntakeSubmissionVariables) {
      return acceptIntakeSubmission(variables.submissionId);
    },
    onSuccess: function invalidate(acceptedSubmission) {
      invalidatePatientIntakeViews(queryClient, acceptedSubmission.patientId);
      queryClient.invalidateQueries({ queryKey: PATIENTS_QUERY_KEY });
      if (acceptedSubmission.patientId === null) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: buildPatientQueryKey(acceptedSubmission.patientId),
      });
      queryClient.invalidateQueries({
        queryKey: buildPatientFirstConsultationQueryKey(acceptedSubmission.patientId),
      });
      queryClient.invalidateQueries({
        queryKey: buildPatientTimelineQueryKey(acceptedSubmission.patientId),
      });
    },
  });
}

interface ReturnIntakeSubmissionVariables {
  submissionId: string;
  reviewNote: string;
}

export function useReturnIntakeSubmissionMutation(): UseMutationResult<
  IntakeSubmission,
  Error,
  ReturnIntakeSubmissionVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function returnSubmission(variables: ReturnIntakeSubmissionVariables) {
      return returnIntakeSubmission(variables.submissionId, variables.reviewNote);
    },
    onSuccess: function invalidate(returnedSubmission) {
      invalidatePatientIntakeViews(queryClient, returnedSubmission.patientId);
    },
  });
}
